import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AdminMetrics,
  type AdminUserListItem,
  type AgentNoteDto,
  type JobDefinition,
  type JobRun,
  type PagedResult,
  type PardotConfigResponse,
  type ProjectsReportResponse,
  type SiteMetricsResponse,
  type StructuresReportResponse,
  type AdvancedReportResponse,
  type StructureTypeAdmin,
  type StructureTypeWrite,
  type UsersReportResponse,
} from '../lib/api';
import { Button, Card, ErrorAlert, TextArea, TextInput } from '../lib/ui';
import { DateRangePicker } from '../lib/DateRangePicker';
import { HoverTooltip } from '../lib/HoverTooltip';

type Tab = 'users-report' | 'projects-report' | 'structures-report' | 'advanced-report' | 'metrics' | 'site-metrics' | 'users' | 'lookups' | 'jobs' | 'notifications' | 'exports' | 'pardot';

const REPORT_TABS: Tab[] = ['users-report', 'projects-report', 'structures-report', 'advanced-report'];

const ALL_TABS: Tab[] = ['users-report', 'projects-report', 'structures-report', 'advanced-report', 'metrics', 'site-metrics', 'users', 'lookups', 'jobs', 'notifications', 'exports', 'pardot'];

// Faithful port of the legacy `metrics/headernav.blade.php` branded icon nav for
// the report console: RETURN HOME + the four report sub-consoles. The active
// item gets the green icon + link-primary + bottom border; the rest are grey.
function ReportConsoleHeader({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const item = (
    target: Tab,
    label: string,
    greenIcon: string,
    greyIcon: string,
  ) => {
    const active = tab === target;
    return (
      <div className={`col-3 text-center border-bottom ${active ? 'border-dark' : ''}`} style={{ flex: '1.7 0 0%' }}>
        <button
          type="button"
          onClick={() => setTab(target)}
          className={`btn btn-link p-0 text-decoration-none fs-6 d-inline-flex align-items-center justify-content-center gap-2 ${active ? 'link-primary' : 'link-dark'}`}
        >
          <img src={active ? greenIcon : greyIcon} alt="" style={{ height: 30, width: 'auto' }} /> {label}
        </button>
      </div>
    );
  };

  return (
    <div className="container-fluid p-0 mb-3">
      <div className="row mt-2 gx-0">
        <div className="col-3 text-center border-bottom">
          <Link className="link-dark fs-6 text-decoration-none d-inline-flex align-items-center justify-content-center gap-2" to="/">
            <img src="/Admin/Icon-Top-Grey-Home.png" alt="" style={{ height: 30, width: 'auto' }} /> RETURN HOME
          </Link>
        </div>
        {item('users-report', 'USERS REPORTS', '/Admin/Icon-Top-Green-Users.png', '/Admin/Icon-Top-Grey-Users.png')}
        {item('projects-report', 'PROJECT REPORTS', '/Admin/Icon-Top-Green-Reports.png', '/Admin/Icon-Top-Grey-Reports.png')}
        {item('structures-report', 'STRUCTURES', '/Admin/Icon-Top-Green-Reports.png', '/Admin/Icon-Top-Grey-Reports.png')}
        {item('advanced-report', 'ADVANCED REPORTS', '/Admin/Icon-Top-Green-Advanced.png', '/Admin/Icon-Top-Grey-Advanced.png')}
      </div>
    </div>
  );
}

export function AdminPage() {
  const { hash } = useLocation();
  const initialTab = (hash.replace('#', '') as Tab);
  const [tab, setTab] = useState<Tab>(ALL_TABS.includes(initialTab) ? initialTab : 'users-report');

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          {REPORT_TABS.includes(tab) && <ReportConsoleHeader tab={tab} setTab={setTab} />}
          {tab === 'users-report' && <UsersReportTab />}
          {tab === 'projects-report' && <ProjectsReportTab />}
          {tab === 'structures-report' && <StructuresReportTab />}
          {tab === 'advanced-report' && <AdvancedReportTab />}
          {tab === 'metrics' && <MetricsTab />}
          {tab === 'site-metrics' && <SiteMetricsTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'lookups' && <LookupsTab />}
          {tab === 'jobs' && <JobsTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'exports' && <ExportsTab />}
          {tab === 'pardot' && <PardotConfigTab />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Users Reports — faithful port of the legacy `metrics.index` console: six KPI
// cards co-located with the USER STATISTICS table, an optional date-range +
// customer-type filter, a filter-aware CSV download, and a suspended toggle.
// ============================================================================
type UserSortKey =
  | 'name' | 'createdAt' | 'loginCount' | 'reportsMade'
  | 'reportsDelivered' | 'freeQuoteRequests' | 'newsOptIn' | 'state';

function UsersReportTab() {
  const qc = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [sortKey, setSortKey] = useState<UserSortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [pageLength, setPageLength] = useState(50);
  const [page, setPage] = useState(1);

  // Reset to the first page whenever the result set changes.
  useEffect(() => { setPage(1); }, [search, from, to, customerTypeId, showHidden, pageLength]);

  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (customerTypeId) params.set('customerTypeId', customerTypeId);
  const qs = params.toString();

  const q = useQuery({
    queryKey: ['admin-users-report', from, to, customerTypeId],
    queryFn: () => api.get<UsersReportResponse>(`/api/v1/admin/site-metrics/users-report${qs ? `?${qs}` : ''}`),
  });

  const suspend = useMutation({
    mutationFn: (vars: { id: number; suspend: boolean }) =>
      api.post(`/api/v1/admin/users/${vars.id}/suspend`, { suspend: vars.suspend }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users-report'] }),
  });

  const csvParams = new URLSearchParams(params);
  if (showHidden) csvParams.set('showHidden', 'true');
  const csvHref = `/api/v1/admin/site-metrics/users-report.csv${csvParams.toString() ? `?${csvParams.toString()}` : ''}`;

  const filterActive = Boolean(from || to || customerTypeId);

  function toggleSort(key: UserSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' || key === 'state' ? 'asc' : 'desc'); }
  }

  const rows = (() => {
    if (!q.data) return [];
    const term = search.trim().toLowerCase();
    let r = q.data.users.filter((u) => (showHidden ? u.suspended != null : u.suspended == null));
    if (term) {
      r = r.filter((u) =>
        u.name.toLowerCase().includes(term) || (u.state ?? '').toLowerCase().includes(term));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  })();

  const ctName = customerTypeId
    ? q.data?.lookups.customerTypes.find((c) => String(c.id) === customerTypeId)?.name
    : undefined;

  // DataTables-style pagination over the filtered/sorted rows.
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageLength));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageLength;
  const pageRows = rows.slice(startIdx, startIdx + pageLength);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + pageLength, total);
  const pageNumbers = (() => {
    const out: number[] = [];
    const lo = Math.max(1, currentPage - 2);
    const hi = Math.min(totalPages, currentPage + 2);
    for (let p = lo; p <= hi; p++) out.push(p);
    return out;
  })();

  return (
    <div>
      {/* Filter bar — faithful port of legacy metrics.index: date range, customer type, CSV */}
      <div className="row mt-1">
        <div className="col-12 col-md-4 mb-2">
          <DateRangePicker
            from={from}
            to={to}
            onApply={(f, t) => { setFrom(f); setTo(t); }}
            onClear={() => { setFrom(''); setTo(''); }}
          />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <select value={customerTypeId} onChange={(e) => setCustomerTypeId(e.target.value)}
            className="form-select" style={{ height: 58 }}>
            <option value="">All Customer Types</option>
            {q.data?.lookups.customerTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 mb-2">
          <a href={csvHref} download className="btn d-grid h-100 btn-primary px-2">
            <span>Download View As CSV</span>
          </a>
        </div>

        {/* Filter status banner */}
        <div className="col-12 pt-2">
          <div className={`alert ${filterActive ? 'alert-danger' : 'alert-primary'} mb-3`} role="alert">
            {filterActive ? (
              <>
                Filter active.
                {ctName ? <> Only showing records with Customer Type of <b>{ctName}</b>.</> : null}
                {from || to ? <> Showing data {from ? <>from <b>{from}</b></> : null}{to ? <> to <b>{to}</b></> : null}.</> : null}
              </>
            ) : 'No filter currently active.'}
          </div>
        </div>
      </div>

      {q.isLoading && <p className="text-muted">Loading…</p>}
      {q.error && <p className="text-danger">Failed to load users report.</p>}

      {q.data && (
        <>
          {/* KPI cards */}
          <div className="row gx-0 justify-content-center mb-3">
            <KpiCard label="Unfiltered Traffic" value={q.data.unfilteredTraffic}
              icon="/Admin/Icon-Card-Entry.png"
              tip="All traffic visiting the calculator." />
            <KpiCard label="Active Traffic" value={q.data.activeTraffic}
              icon="/Admin/Icon-Card-Active.png"
              tip="Total number of visitors that interacted with the site." />
            <KpiCard label="Total Guests" value={q.data.totalGuests}
              icon="/Admin/Icon_TotalGuests.png"
              tip="Visits to the tool where users entered guest mode." />
            <KpiCard label="Total Guests to Users" value={q.data.totalGuestsToUsers}
              icon="/Admin/Icon-Card-New-Users.png"
              tip="Total number of guests that went on to make an account." />
            <KpiCard label="Total Users" value={q.data.totalUsers}
              icon="/Admin/Icon-Card-Users.png"
              tip="Total number of users with logins to the tool." />
            <KpiCard label="News + Product Opt-Ins" value={q.data.newsProductOptIns}
              icon="/Admin/Icon-Card-OptIn.png"
              tip="Total number of users that signed up to receive news and product info when making an account." />
          </div>

          <ErrorAlert message={suspend.isError ? 'Action failed' : null} />

          {/* User statistics table */}
          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 className="mb-0 text-uppercase">User Statistics</h5>
                <div className="d-flex align-items-center gap-3">
                  <label className="d-flex align-items-center gap-1 mb-0 text-muted small">
                    Show
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}
                      value={pageLength} onChange={(e) => setPageLength(Number(e.target.value))}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    entries
                  </label>
                  <TextInput placeholder="Search" value={search}
                    onChange={(e) => setSearch(e.target.value)} className="!w-56" />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <SortTh label="Name" k="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Created At" k="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Logins" k="loginCount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Reports Made" k="reportsMade" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Reports Delivered" k="reportsDelivered" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Free Quote Requests" k="freeQuoteRequests" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="News Opt-In" k="newsOptIn" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="State/Province" k="state" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((u) => (
                      <tr key={u.id}>
                        <td><Link to={`/admin/console/users/${u.id}`} className="link-dark">{u.name}</Link></td>
                        <td className="small">{u.createdAt ? u.createdAt.slice(0, 10) : '—'}</td>
                        <td className="small">{u.loginCount}</td>
                        <td className="small">{u.reportsMade}</td>
                        <td className="small">{u.reportsDelivered}</td>
                        <td className="small">{u.freeQuoteRequests}</td>
                        <td className="small">{u.newsOptIn ? u.newsOptIn.slice(0, 10) : '—'}</td>
                        <td className="small">{u.state ?? '—'}</td>
                        <td className="text-end text-nowrap align-middle">
                          <Link to={`/admin/console/users/${u.id}`} className="mx-2 d-inline-block" title="User information">
                            <img src="/Admin/Icon-Info.png" alt="Info" style={{ maxHeight: 28 }} />
                          </Link>
                          <Link to={`/admin/console/users/${u.id}#reports`} className="mx-2 d-inline-block" title="User reports">
                            <img src="/Admin/Icon-Reports.png" alt="Reports" style={{ maxHeight: 28 }} />
                          </Link>
                          {u.suspended ? (
                            <button type="button" className="btn btn-link p-0 mx-2" title="Restore user"
                              onClick={() => suspend.mutate({ id: u.id, suspend: false })}
                              disabled={suspend.isPending}>
                              <img src="/Admin/Icon-Restore.png" alt="Restore" style={{ maxHeight: 28 }} />
                            </button>
                          ) : (
                            <button type="button" className="btn btn-link p-0 mx-2" title="Suspend user"
                              onClick={() => {
                                if (!confirm(`Suspend ${u.name}?`)) return;
                                suspend.mutate({ id: u.id, suspend: true });
                              }}
                              disabled={suspend.isPending}>
                              <img src="/Admin/Icon-Suspend.png" alt="Suspend" style={{ maxHeight: 28 }} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-muted py-4">No users.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2">
                <span className="text-muted small">
                  Showing {showingFrom} to {showingTo} of {total} entries
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button type="button" className="page-link"
                        onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                      </button>
                    </li>
                    {pageNumbers.map((p) => (
                      <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                        <button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button type="button" className="page-link"
                        onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
              <div className="text-end mt-2">
                <Button variant="secondary" onClick={() => setShowHidden((v) => !v)}>
                  {showHidden ? 'View Not Suspended Users' : 'View Suspended Users'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// CRM drill-down modal — port of the legacy per-project / per-structure
// agent-notes screens (`metrics/report/{id}` + `metrics/structure/{id}`): list
// and add agent notes, and toggle the "quote completed" flag. Reaches the
// existing AdminCrmController endpoints.
// ============================================================================
function CrmModal({ kind, id, name, onClose }: {
  kind: 'projects' | 'structures';
  id: number;
  name: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const base = `/api/v1/admin/${kind}/${id}`;
  const notesKey = ['admin-crm-notes', kind, id] as const;
  const quoteKey = ['admin-crm-quote', kind, id] as const;

  const notesQ = useQuery({
    queryKey: notesKey,
    queryFn: () => api.get<AgentNoteDto[]>(`${base}/agent-notes`),
  });
  const quoteQ = useQuery({
    queryKey: quoteKey,
    queryFn: () => api.get<{ quoteCompleted: string | null }>(`${base}/quote-completed`),
  });

  const addNote = useMutation({
    mutationFn: () => api.post<AgentNoteDto>(`${base}/agent-notes`, { note: note.trim() }),
    onSuccess: () => { setNote(''); qc.invalidateQueries({ queryKey: notesKey }); },
  });
  const setQuote = useMutation({
    mutationFn: (completed: boolean) =>
      api.post<{ quoteCompleted: string | null }>(`${base}/quote-completed`, { completed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: quoteKey }),
  });

  const completed = quoteQ.data?.quoteCompleted ?? null;

  return (
    <div className="modal d-block" tabIndex={-1} role="dialog"
      style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document"
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{kind === 'projects' ? 'Project' : 'Structure'}: {name}</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {/* Quote completed */}
            <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-3">
              <div>
                <strong>Quote completed</strong>
                <div className="small text-muted">
                  {completed ? `Marked completed on ${completed}` : 'Not yet marked completed.'}
                </div>
              </div>
              {completed ? (
                <Button variant="secondary" disabled={setQuote.isPending}
                  onClick={() => setQuote.mutate(false)}>Unmark Quote Completed</Button>
              ) : (
                <Button variant="primary" disabled={setQuote.isPending}
                  onClick={() => setQuote.mutate(true)}>Mark Quote Completed</Button>
              )}
            </div>

            {/* Add agent note */}
            <div className="mb-3">
              <label className="form-label">Add agent note</label>
              <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Internal note about this quote…" />
              <div className="text-end mt-2">
                <Button variant="primary" disabled={!note.trim() || addNote.isPending}
                  onClick={() => addNote.mutate()}>Add Note</Button>
              </div>
            </div>

            {/* Notes list */}
            <h6 className="text-uppercase">Agent notes</h6>
            {notesQ.isLoading && <p className="text-muted">Loading…</p>}
            {notesQ.data && notesQ.data.length === 0 && (
              <p className="text-muted">No agent notes yet.</p>
            )}
            <ul className="list-group">
              {notesQ.data?.map((n) => (
                <li key={n.id} className="list-group-item">
                  <div className="d-flex justify-content-between">
                    <strong>{n.userName ?? `User #${n.userId}`}</strong>
                    <span className="small text-muted">
                      {n.createdAt ? n.createdAt.slice(0, 16).replace('T', ' ') : ''}
                    </span>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{n.note}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tip, icon }: { label: string; value: number; tip: string; icon?: string }) {
  return (
    <div className="col-12 col-md-6 col-lg-4 col-xl-4">
      <div className="row border m-2 text-success h-100">
        {icon && (
          <div className="col px-0 d-flex justify-content-center align-items-center">
            <img className="img-fluid" src={icon} alt="" />
          </div>
        )}
        <div className="col-7 my-3 d-flex align-items-center">
          <h3 className="my-0 mb-0 pt-1">{label}</h3>
        </div>
        <div className="col d-flex align-items-center me-2">
          <h3 className="pt-1 mb-0">{value.toLocaleString()}</h3>
          <HoverTooltip text={tip}>
            <i className="mdi mdi-information-outline fs-4 ps-2"
              style={{ cursor: 'help' }} aria-label={tip} />
          </HoverTooltip>
        </div>
      </div>
    </div>
  );
}

function SortTh({ label, k, sortKey, sortDir, onSort }: {
  label: string;
  k: UserSortKey;
  sortKey: UserSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: UserSortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th role="button" onClick={() => onSort(k)} className="user-select-none">
      {label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

// ============================================================================
// Project Reports — port of the legacy `metrics.reportindex` console: seven KPI
// cards (Projects Started/Finished/Updated/Total, Quote Requests, Projects
// Downloaded/Emailed) plus a per-project status table, filterable by date range
// and customer type with a filter-aware CSV download.
// ============================================================================
type ProjectSortKey = 'name' | 'status' | 'hasAgentNotes' | 'createdAt' | 'updatedAt';

function ProjectsReportTab() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<ProjectSortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [crmRow, setCrmRow] = useState<{ id: number; name: string } | null>(null);

  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (customerTypeId) params.set('customerTypeId', customerTypeId);
  const qs = params.toString();

  const q = useQuery({
    queryKey: ['admin-projects-report', from, to, customerTypeId],
    queryFn: () => api.get<ProjectsReportResponse>(`/api/v1/admin/site-metrics/projects-report${qs ? `?${qs}` : ''}`),
  });

  const csvHref = `/api/v1/admin/site-metrics/projects-report.csv${qs ? `?${qs}` : ''}`;
  const filterActive = Boolean(from || to || customerTypeId);
  const ctName = customerTypeId
    ? q.data?.lookups.customerTypes.find((c) => String(c.id) === customerTypeId)?.name
    : undefined;

  function toggleSort(key: ProjectSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' || key === 'status' ? 'asc' : 'desc'); }
  }

  const rows = (() => {
    if (!q.data) return [];
    const term = search.trim().toLowerCase();
    let r = q.data.projects;
    if (term) {
      r = r.filter((p) =>
        p.name.toLowerCase().includes(term) ||
        p.status.toLowerCase().includes(term) ||
        (p.ownerName ?? '').toLowerCase().includes(term));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'boolean' && typeof bv === 'boolean') return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  })();

  function statusClass(status: string): string {
    if (status.includes('Quote Requested')) return 'bg-warning';
    if (status.includes('Quote Completed')) return 'bg-primary text-white';
    return '';
  }

  return (
    <div>
      {/* Filter bar — matches Users Report: date range, customer type, CSV */}
      <div className="row mt-1">
        <div className="col-12 col-md-4 mb-2">
          <DateRangePicker
            from={from}
            to={to}
            onApply={(f, t) => { setFrom(f); setTo(t); }}
            onClear={() => { setFrom(''); setTo(''); }}
          />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <select value={customerTypeId} onChange={(e) => setCustomerTypeId(e.target.value)}
            className="form-select" style={{ height: 58 }}>
            <option value="">All Customer Types</option>
            {q.data?.lookups.customerTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 mb-2">
          <a href={csvHref} download className="btn d-grid h-100 btn-primary px-2">
            <span>Download View As CSV</span>
          </a>
        </div>

        {/* Filter status banner */}
        <div className="col-12 pt-2">
          <div className={`alert ${filterActive ? 'alert-danger' : 'alert-primary'} mb-3`} role="alert">
            {filterActive ? (
              <>
                Filter active.
                {ctName ? <> Only showing records with Customer Type of <b>{ctName}</b>.</> : null}
                {from || to ? <> Showing data {from ? <>from <b>{from}</b></> : null}{to ? <> to <b>{to}</b></> : null}.</> : null}
              </>
            ) : 'No filter currently active.'}
          </div>
        </div>
      </div>

      {q.isLoading && <p className="text-muted">Loading…</p>}
      {q.error && <p className="text-danger">Failed to load project report.</p>}

      {q.data && (
        <>
          {/* KPI cards */}
          <div className="row g-2 mb-3">
            <KpiCard label="Projects Started" value={q.data.projectsStarted}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of projects started." />
            <KpiCard label="Projects Finished" value={q.data.projectsFinished}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of projects finished." />
            <KpiCard label="Updated Projects" value={q.data.projectsUpdated}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of projects updated or modified (may include Projects Started)." />
            <KpiCard label="Total Projects" value={q.data.totalProjects}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of projects generated by the tool." />
            <KpiCard label="Quote Requests Started" value={q.data.quoteRequests}
              icon="/Admin/Icon-Card-Quote.png"
              tip="Number of times a free quote has been requested from the download and share options in a report." />
            <KpiCard label="Projects Downloaded" value={q.data.projectsDownloaded}
              icon="/Admin/Icon-Card-Saved.png"
              tip="Number of times projects have been downloaded from the download and share options in a report." />
            <KpiCard label="Projects Emailed" value={q.data.projectsEmailed}
              icon="/Admin/Icon-Card-Email.png"
              tip="Number of times a report has been emailed from the download and share options in a report." />
          </div>

          {/* Project status table */}
          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 className="mb-0 text-uppercase">Projects</h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">{rows.length} shown</span>
                  <TextInput placeholder="Search" value={search}
                    onChange={(e) => setSearch(e.target.value)} className="!w-56" />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <ProjectSortTh label="Project Name" k="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <ProjectSortTh label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <ProjectSortTh label="Agent Notes" k="hasAgentNotes" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <ProjectSortTh label="Created At" k="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <ProjectSortTh label="Updated At" k="updatedAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <th className="text-end">Owner</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.id}>
                        <td title={p.name}>
                          <button type="button" className="btn btn-link p-0 link-dark text-start"
                            onClick={() => setCrmRow({ id: p.id, name: p.name })}>
                            {p.name.length > 45 ? `${p.name.slice(0, 45)}…` : p.name}
                          </button>
                        </td>
                        <td><span className={`d-inline-block px-1 ${statusClass(p.status)}`}>{p.status}</span></td>
                        <td className="small">{p.hasAgentNotes ? 'Yes' : 'No'}</td>
                        <td className="small text-nowrap">{p.createdAt ? p.createdAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="small text-nowrap">{p.updatedAt ? p.updatedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="text-end">
                          <Link to={`/admin/console/users/${p.userId}`} className="link-dark">{p.ownerName ?? `#${p.userId}`}</Link>
                        </td>
                        <td className="text-end align-middle">
                          <button type="button" className="btn btn-link p-0 mx-2" title="Project notes &amp; quote"
                            onClick={() => setCrmRow({ id: p.id, name: p.name })}>
                            <img src="/Admin/Icon-Info.png" alt="Details" style={{ maxHeight: 28 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted py-4">No projects.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {crmRow && (
        <CrmModal kind="projects" id={crmRow.id} name={crmRow.name} onClose={() => setCrmRow(null)} />
      )}
    </div>
  );
}

function ProjectSortTh({ label, k, sortKey, sortDir, onSort }: {
  label: string;
  k: ProjectSortKey;
  sortKey: ProjectSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: ProjectSortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th role="button" onClick={() => onSort(k)} className="user-select-none">
      {label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

// ============================================================================
// Structures Reports
// ============================================================================
type StructureSortKey = 'name' | 'status' | 'hasAgentNotes' | 'createdAt' | 'updatedAt';

function StructuresReportTab() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<StructureSortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [crmRow, setCrmRow] = useState<{ id: number; name: string } | null>(null);

  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (customerTypeId) params.set('customerTypeId', customerTypeId);
  const qs = params.toString();

  const q = useQuery({
    queryKey: ['admin-structures-report', from, to, customerTypeId],
    queryFn: () => api.get<StructuresReportResponse>(`/api/v1/admin/site-metrics/structures-report${qs ? `?${qs}` : ''}`),
  });

  const csvHref = `/api/v1/admin/site-metrics/structures-report.csv${qs ? `?${qs}` : ''}`;
  const filterActive = Boolean(from || to || customerTypeId);
  const ctName = customerTypeId
    ? q.data?.lookups.customerTypes.find((c) => String(c.id) === customerTypeId)?.name
    : undefined;

  function toggleSort(key: StructureSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' || key === 'status' ? 'asc' : 'desc'); }
  }

  const rows = (() => {
    if (!q.data) return [];
    const term = search.trim().toLowerCase();
    let r = q.data.structures;
    if (term) {
      r = r.filter((s) =>
        s.name.toLowerCase().includes(term) ||
        s.status.toLowerCase().includes(term) ||
        (s.ownerName ?? '').toLowerCase().includes(term));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'boolean' && typeof bv === 'boolean') return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  })();

  function statusClass(status: string): string {
    if (status.includes('Quote Requested')) return 'bg-warning';
    if (status.includes('Quote Completed')) return 'bg-primary text-white';
    return '';
  }

  return (
    <div>
      {/* Filter bar — matches Users Report: date range, customer type, CSV */}
      <div className="row mt-1">
        <div className="col-12 col-md-4 mb-2">
          <DateRangePicker
            from={from}
            to={to}
            onApply={(f, t) => { setFrom(f); setTo(t); }}
            onClear={() => { setFrom(''); setTo(''); }}
          />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <select value={customerTypeId} onChange={(e) => setCustomerTypeId(e.target.value)}
            className="form-select" style={{ height: 58 }}>
            <option value="">All Customer Types</option>
            {q.data?.lookups.customerTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 mb-2">
          <a href={csvHref} download className="btn d-grid h-100 btn-primary px-2">
            <span>Download View As CSV</span>
          </a>
        </div>

        {/* Filter status banner */}
        <div className="col-12 pt-2">
          <div className={`alert ${filterActive ? 'alert-danger' : 'alert-primary'} mb-3`} role="alert">
            {filterActive ? (
              <>
                Filter active.
                {ctName ? <> Only showing records with Customer Type of <b>{ctName}</b>.</> : null}
                {from || to ? <> Showing data {from ? <>from <b>{from}</b></> : null}{to ? <> to <b>{to}</b></> : null}.</> : null}
              </>
            ) : 'No filter currently active.'}
          </div>
        </div>
      </div>

      {q.isLoading && <p className="text-muted">Loading…</p>}
      {q.error && <p className="text-danger">Failed to load structure report.</p>}

      {q.data && (
        <>
          {/* KPI cards */}
          <div className="row g-2 mb-3">
            <KpiCard label="Structures Started" value={q.data.structuresStarted}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of structures started." />
            <KpiCard label="Structures Finished" value={q.data.structuresFinished}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of structures finished." />
            <KpiCard label="Updated Structures" value={q.data.structuresUpdated}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of structures updated or modified (may include Structures Started)." />
            <KpiCard label="Total Structures" value={q.data.totalStructures}
              icon="/Admin/Icon-Card-Reports.png"
              tip="Total number of structures generated by the tool." />
            <KpiCard label="Quote Requests Started" value={q.data.quoteRequests}
              icon="/Admin/Icon-Card-Quote.png"
              tip="Number of times a free quote has been requested from the download and share options in a report." />
            <KpiCard label="Structures Downloaded" value={q.data.structuresDownloaded}
              icon="/Admin/Icon-Card-Saved.png"
              tip="Number of times structures have been downloaded from the download and share options in a report." />
            <KpiCard label="Structures Emailed" value={q.data.structuresEmailed}
              icon="/Admin/Icon-Card-Email.png"
              tip="Number of times a report has been emailed from the download and share options in a report." />
          </div>

          {/* Structure status table */}
          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 className="mb-0 text-uppercase">Structures</h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">{rows.length} shown</span>
                  <TextInput placeholder="Search" value={search}
                    onChange={(e) => setSearch(e.target.value)} className="!w-56" />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <StructureSortTh label="Project Name" k="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <StructureSortTh label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <StructureSortTh label="Agent Notes" k="hasAgentNotes" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <StructureSortTh label="Date Created" k="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <StructureSortTh label="Last Updated On" k="updatedAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                      <th className="text-end">Owner</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.id}>
                        <td title={s.name}>
                          <button type="button" className="btn btn-link p-0 link-dark text-start"
                            onClick={() => setCrmRow({ id: s.id, name: s.name })}>
                            {s.name.length > 45 ? `${s.name.slice(0, 45)}…` : s.name}
                          </button>
                        </td>
                        <td><span className={`d-inline-block px-1 ${statusClass(s.status)}`}>{s.status}</span></td>
                        <td className="small">{s.hasAgentNotes ? 'Yes' : 'No'}</td>
                        <td className="small text-nowrap">{s.createdAt ? s.createdAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="small text-nowrap">{s.updatedAt ? s.updatedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td className="text-end">
                          <Link to={`/admin/console/users/${s.userId}`} className="link-dark">{s.ownerName ?? `#${s.userId}`}</Link>
                        </td>
                        <td className="text-end align-middle">
                          <button type="button" className="btn btn-link p-0 mx-2" title="Structure notes &amp; quote"
                            onClick={() => setCrmRow({ id: s.id, name: s.name })}>
                            <img src="/Admin/Icon-Info.png" alt="Details" style={{ maxHeight: 28 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted py-4">No structures.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {crmRow && (
        <CrmModal kind="structures" id={crmRow.id} name={crmRow.name} onClose={() => setCrmRow(null)} />
      )}
    </div>
  );
}

function StructureSortTh({ label, k, sortKey, sortDir, onSort }: {
  label: string;
  k: StructureSortKey;
  sortKey: StructureSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: StructureSortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th role="button" onClick={() => onSort(k)} className="user-select-none">
      {label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

// ============================================================================
// Advanced Reports — user counts broken down by customer type
// ============================================================================
function AdvancedReportTab() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState('');

  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (customerTypeId) params.set('customerTypeId', customerTypeId);
  const qs = params.toString();

  const q = useQuery({
    queryKey: ['admin-advanced-report', from, to, customerTypeId],
    queryFn: () => api.get<AdvancedReportResponse>(`/api/v1/admin/site-metrics/advanced-report${qs ? `?${qs}` : ''}`),
  });

  const csvHref = `/api/v1/admin/site-metrics/advanced-report.csv${qs ? `?${qs}` : ''}`;
  const filterActive = Boolean(from || to || customerTypeId);
  const ctName = customerTypeId
    ? q.data?.lookups.customerTypes.find((c) => String(c.id) === customerTypeId)?.name
    : undefined;

  return (
    <div>
      {/* Filter bar — matches Users Report: date range, customer type, CSV */}
      <div className="row mt-1">
        <div className="col-12 col-md-4 mb-2">
          <DateRangePicker
            from={from}
            to={to}
            onApply={(f, t) => { setFrom(f); setTo(t); }}
            onClear={() => { setFrom(''); setTo(''); }}
          />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <select value={customerTypeId} onChange={(e) => setCustomerTypeId(e.target.value)}
            className="form-select" style={{ height: 58 }}>
            <option value="">All Customer Types</option>
            {q.data?.lookups.customerTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 mb-2">
          <a href={csvHref} download className="btn d-grid h-100 btn-primary px-2">
            <span>Download View As CSV</span>
          </a>
        </div>

        {/* Filter status banner */}
        <div className="col-12 pt-2">
          <div className={`alert ${filterActive ? 'alert-danger' : 'alert-primary'} mb-3`} role="alert">
            {filterActive ? (
              <>
                Filter active.
                {ctName ? <> Only showing records with Customer Type of <b>{ctName}</b>.</> : null}
                {from || to ? <> Showing data {from ? <>from <b>{from}</b></> : null}{to ? <> to <b>{to}</b></> : null}.</> : null}
              </>
            ) : 'No filter currently active.'}
          </div>
        </div>
      </div>

      {q.isLoading && <p className="text-muted">Loading…</p>}
      {q.error && <p className="text-danger">Failed to load advanced report.</p>}

      {q.data && (
        <div className="row g-2 mb-3">
          {q.data.buckets.map((b) => (
            <KpiCard key={b.customerType} label={`${b.customerType} Users`} value={b.count}
              icon={advancedReportIcon(b.customerType)}
              tip={`Total ${b.customerType} users registered in the tool.`} />
          ))}
          <KpiCard label="Total Users" value={q.data.totalUsers}
            icon="/Admin/Icon-Card-Users.png"
            tip="Total number of users across all customer types (excludes the guest account)." />
        </div>
      )}
    </div>
  );
}

// Maps a customer-type name to its legacy branded advanced-report icon
// (`metrics/advancedreports.blade.php`). Falls back to the generic users icon.
function advancedReportIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('architect')) return '/Admin/Icon-Card-Architect.png';
  if (n.includes('contractor')) return '/Admin/Icon-Card-Contractor.png';
  if (n.includes('distributor')) return '/Admin/Icon-Card-Distributor.png';
  if (n.includes('engineer')) return '/Admin/Icon-Card-Engineer.png';
  if (n.includes('government')) return '/Admin/Icon-Card-Government.png';
  if (n.includes('homeowner') || n.includes('home owner')) return '/Admin/Icon-Card-Homeowner.png';
  if (n.includes('employee') || n.includes('ads')) return '/Admin/Icon-Card-Employee.png';
  return '/Admin/Icon-Card-Users.png';
}

// ============================================================================
// Metrics
// ============================================================================
function MetricsTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: () => api.get<AdminMetrics>('/api/v1/admin/metrics'),
  });
  if (isLoading) return <p className="text-muted">Loading…</p>;
  if (error || !data) return <p className="text-danger">Failed to load metrics.</p>;

  return (
    <div>
      <div className="row g-2 mb-3">
        <Stat label="Total users" value={data.totalUsers} />
        <Stat label="Active users" value={data.activeUsers} />
        <Stat label="Suspended" value={data.suspendedUsers} accent="amber" />
        <Stat label="Total projects" value={data.totalProjects} />
        <Stat label="Total structures" value={data.totalStructures} />
        <Stat label="New users (7d)" value={data.recentSignups7d} accent="green" />
        <Stat label="New projects (7d)" value={data.recentProjects7d} accent="green" />
        <Stat label="New structures (7d)" value={data.recentStructures7d} accent="green" />
      </div>

      <Card title="Sign-ups (last 14 days)">
        <Bars data={data.signupsByDay} />
      </Card>
      <Card title="Projects created (last 14 days)">
        <Bars data={data.projectsByDay} />
      </Card>
    </div>
  );
}

function Stat({ label, value, accent }: {
  label: string;
  value: number | string;
  accent?: 'green' | 'amber';
}) {
  const tone = accent === 'green' ? 'text-success'
    : accent === 'amber' ? 'text-warning' : 'text-dark';
  return (
    <div className="col-6 col-md-3">
      <div className="card mb-0">
        <div className="card-body">
          <div className="text-muted small text-uppercase">{label}</div>
          <div className={`mt-1 fs-3 fw-semibold ${tone}`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
        </div>
      </div>
    </div>
  );
}

function Bars({ data }: { data: { date: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="d-flex align-items-end gap-1" style={{ height: 128 }}>
      {data.map((d) => (
        <div
          key={d.date}
          className="flex-fill d-flex flex-column align-items-center gap-1"
          title={`${d.date}: ${d.count}`}
        >
          <div
            className="w-100 bg-primary rounded-top"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }}
          />
          <div className="text-muted" style={{ fontSize: 9 }}>{d.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Users
// ============================================================================
function UsersTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'suspended'>('all');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const list = useQuery({
    queryKey: ['admin-users', { search, status, page }],
    queryFn: () => api.get<PagedResult<AdminUserListItem>>(
      `/api/v1/admin/users?search=${encodeURIComponent(search)}&status=${status}&page=${page}&pageSize=${pageSize}`,
    ),
  });

  const suspend = useMutation({
    mutationFn: (vars: { id: number; suspend: boolean }) =>
      api.post(`/api/v1/admin/users/${vars.id}/suspend`, { suspend: vars.suspend }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
  });

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <TextInput
          placeholder="Search name / email / company"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="!w-72"
        />
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value as typeof status); setPage(1); }}
          className="form-select form-select-sm"
          style={{ width: 160 }}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <span className="text-muted small">
          {list.data ? `${list.data.total} user${list.data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      <ErrorAlert message={suspend.isError ? 'Action failed' : null} />

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Roles</th>
                  <th>Logins</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">Loading…</td></tr>
                )}
                {list.data?.items.map((u) => (
                  <tr key={u.id}>
                    <td className="text-muted small">{u.id}</td>
                    <td><Link to={`/admin/console/users/${u.id}`}>{u.name}</Link></td>
                    <td>{u.email}</td>
                    <td>{u.company}</td>
                    <td>
                      {u.roles.length === 0 ? <span className="text-muted">—</span> : (
                        <span className="d-inline-flex gap-1 flex-wrap">
                          {u.roles.map((r) => (
                            <span key={r} className="badge bg-secondary text-uppercase">{r}</span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="small">{u.loginCount}</td>
                    <td className="small">
                      {u.suspended ? <span className="text-warning">Suspended {u.suspended}</span> : <span className="text-success">Active</span>}
                    </td>
                    <td className="text-end">
                      {u.suspended ? (
                        <Button
                          variant="secondary"
                          onClick={() => suspend.mutate({ id: u.id, suspend: false })}
                          disabled={suspend.isPending}
                        >
                          Reinstate
                        </Button>
                      ) : (
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (!confirm(`Suspend ${u.name}?`)) return;
                            suspend.mutate({ id: u.id, suspend: true });
                          }}
                          disabled={suspend.isPending}
                        >
                          Suspend
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {list.data && list.data.items.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">No users.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {list.data && list.data.total > pageSize && (
        <div className="d-flex align-items-center justify-content-between text-muted small mt-3">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</Button>
          <span>Page {page} of {Math.ceil(list.data.total / pageSize)}</span>
          <Button variant="secondary" disabled={page * pageSize >= list.data.total} onClick={() => setPage((p) => p + 1)}>Next →</Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Lookups (Structure Types)
// ============================================================================
function LookupsTab() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-structure-types'],
    queryFn: () => api.get<StructureTypeAdmin[]>('/api/v1/admin/lookups/structure-types'),
  });

  const [editing, setEditing] = useState<StructureTypeAdmin | 'new' | null>(null);

  const save = useMutation({
    mutationFn: (vars: { id: number | null; body: StructureTypeWrite }) =>
      vars.id
        ? api.put<StructureTypeAdmin>(`/api/v1/admin/lookups/structure-types/${vars.id}`, vars.body)
        : api.post<StructureTypeAdmin>('/api/v1/admin/lookups/structure-types', vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-structure-types'] });
      setEditing(null);
    },
  });

  const del = useMutation({
    mutationFn: (id: number) => api.del(`/api/v1/admin/lookups/structure-types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-structure-types'] }),
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Structure types</h5>
        <Button onClick={() => setEditing('new')}>+ New type</Button>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>Family</th>
                  <th>Name</th>
                  <th>Abbr.</th>
                  <th>In use</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading && <tr><td colSpan={5} className="text-center text-muted py-4">Loading…</td></tr>}
                {list.data?.map((t) => (
                  <tr key={t.id}>
                    <td>{t.family}</td>
                    <td>{t.name}</td>
                    <td className="text-muted small">{t.abbreviation ?? '—'}</td>
                    <td className="small">{t.structureCount}</td>
                    <td className="text-end">
                      <Button variant="secondary" onClick={() => setEditing(t)}>Edit</Button>
                      <span className="mx-1" />
                      <Button
                        variant="danger"
                        disabled={t.structureCount > 0}
                        onClick={() => {
                          if (!confirm(`Delete ${t.name}?`)) return;
                          del.mutate(t.id);
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {list.data && list.data.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No structure types.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && (
        <div className="mt-3">
          <StructureTypeEditor
            initial={editing === 'new' ? null : editing}
            saving={save.isPending}
            onCancel={() => setEditing(null)}
            onSave={(body) => save.mutate({ id: editing === 'new' ? null : editing.id, body })}
          />
        </div>
      )}
    </div>
  );
}

function StructureTypeEditor({
  initial,
  saving,
  onCancel,
  onSave,
}: {
  initial: StructureTypeAdmin | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: StructureTypeWrite) => void;
}) {
  const [form, setForm] = useState<StructureTypeWrite>(() => ({
    name: initial?.name ?? '',
    family: initial?.family ?? '',
    abbreviation: initial?.abbreviation ?? '',
    is4SeatSquare: initial?.is4SeatSquare ?? false,
    is2SeatLong: initial?.is2SeatLong ?? false,
    is2SeatShort: initial?.is2SeatShort ?? false,
    isConcrete: initial?.isConcrete ?? false,
    isCastIron: initial?.isCastIron ?? false,
    isCastIronRound: initial?.isCastIronRound ?? false,
    isCastIronRectangular: initial?.isCastIronRectangular ?? false,
    isCastIronCurbBoxCombo: initial?.isCastIronCurbBoxCombo ?? false,
    isOpenThroat: initial?.isOpenThroat ?? false,
    isNyloplast: initial?.isNyloplast ?? false,
    enabledFields: initial?.enabledFields ?? '',
    extraOutputs: initial?.extraOutputs ?? '',
  }));

  const flags: { key: keyof StructureTypeWrite; label: string }[] = [
    { key: 'is4SeatSquare', label: '4-seat square' },
    { key: 'is2SeatLong', label: '2-seat long' },
    { key: 'is2SeatShort', label: '2-seat short' },
    { key: 'isConcrete', label: 'Concrete' },
    { key: 'isCastIron', label: 'Cast iron' },
    { key: 'isCastIronRound', label: 'Cast iron round' },
    { key: 'isCastIronRectangular', label: 'Cast iron rectangular' },
    { key: 'isCastIronCurbBoxCombo', label: 'Curb box combo' },
    { key: 'isOpenThroat', label: 'Open throat' },
    { key: 'isNyloplast', label: 'Nyloplast' },
  ];

  return (
    <div className="card border-primary">
      <div className="card-body bg-light">
        <h5 className="mb-3 text-primary">
          {initial ? `Edit ${initial.name}` : 'New structure type'}
        </h5>
        <div className="row g-3">
          <div className="col-md-6"><Field label="Family"><TextInput value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} /></Field></div>
          <div className="col-md-6"><Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
          <div className="col-md-6"><Field label="Abbreviation"><TextInput value={form.abbreviation ?? ''} onChange={(e) => setForm({ ...form, abbreviation: e.target.value })} /></Field></div>
          <div className="col-md-6"><Field label="Enabled fields (CSV)"><TextInput value={form.enabledFields} onChange={(e) => setForm({ ...form, enabledFields: e.target.value })} /></Field></div>
          <div className="col-md-6"><Field label="Extra outputs (CSV)"><TextInput value={form.extraOutputs} onChange={(e) => setForm({ ...form, extraOutputs: e.target.value })} /></Field></div>
        </div>
        <div className="row g-2 mt-2">
          {flags.map(({ key, label }) => (
            <div key={key} className="col-6 col-md-4">
              <div className="form-check">
                <input
                  id={`flag-${key}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(form[key])}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor={`flag-${key}`}>{label}</label>
              </div>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button disabled={saving || !form.name || !form.family} onClick={() => onSave(form)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="form-label small fw-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

// ============================================================================
// Jobs
// ============================================================================
function JobsTab() {
  const qc = useQueryClient();
  const defs = useQuery({
    queryKey: ['admin-job-defs'],
    queryFn: () => api.get<JobDefinition[]>('/api/v1/admin/jobs/definitions'),
  });
  const runs = useQuery({
    queryKey: ['admin-job-runs'],
    queryFn: () => api.get<JobRun[]>('/api/v1/admin/jobs/runs?take=50'),
    refetchInterval: 5000,
  });
  const trigger = useMutation({
    mutationFn: (name: string) => api.post<JobRun>(`/api/v1/admin/jobs/${name}/run`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-job-runs'] }),
  });

  return (
    <div>
      <Card title="Available jobs">
        <div className="d-flex flex-column gap-2">
          {defs.data?.map((d) => (
            <div key={d.name} className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
              <code className="small text-dark">{d.name}</code>
              <Button
                onClick={() => trigger.mutate(d.name)}
                disabled={trigger.isPending}
              >
                {trigger.isPending && trigger.variables === d.name ? 'Running…' : 'Run now'}
              </Button>
            </div>
          ))}
          {defs.isLoading && <p className="text-muted mb-0">Loading…</p>}
        </div>
      </Card>

      <ErrorAlert message={trigger.isError ? 'Job trigger failed' : null} />

      <Card title="Recent runs">
        <div className="table-responsive">
          <table className="table table-striped table-hover mb-0">
            <thead>
              <tr>
                <th>Job</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Items</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {runs.isLoading && <tr><td colSpan={7} className="text-center text-muted py-4">Loading…</td></tr>}
              {runs.data?.map((r) => {
                const dur = r.finishedAt
                  ? Math.max(0, new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime())
                  : null;
                const status = r.success === null ? 'Running' : r.success ? 'OK' : 'Failed';
                const tone = r.success === null ? 'text-info'
                  : r.success ? 'text-success' : 'text-danger';
                return (
                  <tr key={r.id}>
                    <td><code className="small">{r.jobName}</code></td>
                    <td className="small">{new Date(r.startedAt).toLocaleString()}</td>
                    <td className="small">{dur === null ? '—' : `${dur} ms`}</td>
                    <td className="small text-muted">{r.trigger}</td>
                    <td className={`small fw-medium ${tone}`}>{status}</td>
                    <td className="small">{r.itemsProcessed}</td>
                    <td className="small">{r.message ?? '—'}</td>
                  </tr>
                );
              })}
              {runs.data && runs.data.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-4">No runs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}



// ============================================================================
// Notifications
// ============================================================================
function NotificationsTab() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: () => api.get<import('../lib/api').NotificationDto[]>('/api/v1/notifications/all'),
  });

  const [form, setForm] = useState<{ group: string; bodyHtml: string; dismissable: boolean; displayAfter: string }>({
    group: '', bodyHtml: '', dismissable: true, displayAfter: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post('/api/v1/notifications', {
      group: form.group.trim() || null,
      bodyHtml: form.bodyHtml,
      dismissable: form.dismissable,
      displayAfter: form.displayAfter ? new Date(form.displayAfter).toISOString() : null,
    }),
    onSuccess: () => {
      setForm({ group: '', bodyHtml: '', dismissable: true, displayAfter: '' });
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin-notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-active'] });
    },
    onError: () => setError('Could not create notification.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/api/v1/notifications/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-active'] });
    },
  });

  return (
    <div>
      <Card title="New banner" actions={
        <Button disabled={create.isPending || !form.bodyHtml.trim()} onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      }>
        <ErrorAlert message={error} />
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small">Group</label>
            <select className="form-select" value={form.group}
              onChange={(e) => setForm({ ...form, group: e.target.value })}>
              <option value="">All groups</option>
              <option value="Super Admin">Super Admin</option>
              <option value="Admin">Admin</option>
              <option value="Users">Users</option>
              <option value="Guest">Guest</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label small">Display after (optional)</label>
            <TextInput type="datetime-local" value={form.displayAfter} onChange={(e) => setForm({ ...form, displayAfter: e.target.value })} />
          </div>
        </div>
        <div className="mt-3">
          <label className="form-label small">Body HTML</label>
          <textarea
            value={form.bodyHtml}
            onChange={(e) => setForm({ ...form, bodyHtml: e.target.value })}
            rows={4}
            className="form-control font-monospace small"
            placeholder="<p>Scheduled maintenance <strong>tonight</strong>.</p>"
          />
        </div>
        <div className="form-check mt-3">
          <input id="notif-dismiss" className="form-check-input" type="checkbox" checked={form.dismissable} onChange={(e) => setForm({ ...form, dismissable: e.target.checked })} />
          <label className="form-check-label" htmlFor="notif-dismiss">Dismissable</label>
        </div>
      </Card>

      <Card title={`Existing banners (${list.data?.length ?? 0})`}>
        <div className="d-flex flex-column gap-2">
          {list.isLoading && <p className="text-muted mb-0">Loading…</p>}
          {list.data?.map((n) => (
            <div key={n.id} className="d-flex align-items-start gap-3 border rounded p-3">
              <div className="flex-grow-1">
                <div className="small text-muted">
                  #{n.id}
                  {n.group ? ` · ${n.group}` : ''}
                  {n.displayAfter ? ` · after ${new Date(n.displayAfter).toLocaleString()}` : ''}
                  {n.dismissable ? ' · dismissable' : ' · sticky'}
                </div>
                <div dangerouslySetInnerHTML={{ __html: n.bodyHtml }} />
              </div>
              <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(n.id)}>Delete</Button>
            </div>
          ))}
          {list.data && list.data.length === 0 && (
            <p className="text-muted mb-0">No banners yet.</p>
          )}
        </div>
      </Card>
    </div>
  );
}


// ============================================================================
// Exports
// ============================================================================
function ExportsTab() {
  const downloads: { label: string; url: string; description: string }[] = [
    { label: 'Users', url: '/api/v1/admin/exports/users.csv', description: 'All registered users with contact details, login counts, suspension state.' },
    { label: 'Projects', url: '/api/v1/admin/exports/projects.csv', description: 'All non-deleted project folders with delivery + quote-completed timestamps.' },
    { label: 'Structures', url: '/api/v1/admin/exports/structures.csv', description: 'All non-deleted structures with quote and delivery timestamps.' },
  ];
  return (
    <div>
      <p className="text-muted">CSV exports for offline analysis. Files are streamed live from the database.</p>
      <div className="row g-3">
        {downloads.map((d) => (
          <div key={d.url} className="col-md-4">
            <a
              href={d.url}
              className="card text-decoration-none h-100"
              download
            >
              <div className="card-body">
                <h5 className="card-title mb-2">{d.label}.csv</h5>
                <p className="text-muted small mb-2">{d.description}</p>
                <span className="text-primary small fw-medium">Download →</span>
              </div>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Site Metrics — mirrors legacy /metrics dashboard. KPIs over a date range +
// per-day stacked bars for unfiltered/active/guest traffic. Filterable by
// attribute and customer type.
// ============================================================================
function SiteMetricsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const defaultFrom = new Date(Date.now() - 29 * 86400_000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const [attributeId, setAttributeId] = useState<string>('');
  const [customerTypeId, setCustomerTypeId] = useState<string>('');

  const params = new URLSearchParams({ from, to });
  if (attributeId) params.set('attributeId', attributeId);
  if (customerTypeId) params.set('customerTypeId', customerTypeId);

  const q = useQuery({
    queryKey: ['admin-site-metrics', from, to, attributeId, customerTypeId],
    queryFn: () => api.get<SiteMetricsResponse>(`/api/v1/admin/site-metrics?${params.toString()}`),
  });

  if (q.isLoading) return <p className="text-muted">Loading…</p>;
  if (q.error || !q.data) return <p className="text-danger">Failed to load site metrics.</p>;
  const d = q.data;

  return (
    <div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label className="form-label small mb-1">From</label>
              <input type="date" value={from} max={to}
                onChange={(e) => setFrom(e.target.value)}
                className="form-control form-control-sm" />
            </div>
            <div>
              <label className="form-label small mb-1">To</label>
              <input type="date" value={to} min={from} max={today}
                onChange={(e) => setTo(e.target.value)}
                className="form-control form-control-sm" />
            </div>
            <div>
              <label className="form-label small mb-1">Attribute</label>
              <select value={attributeId} onChange={(e) => setAttributeId(e.target.value)}
                className="form-select form-select-sm">
                <option value="">All</option>
                {d.lookups.attributes.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label small mb-1">Customer type</label>
              <select value={customerTypeId} onChange={(e) => setCustomerTypeId(e.target.value)}
                className="form-select form-select-sm">
                <option value="">All</option>
                {d.lookups.customerTypes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <span className="ms-auto text-muted small">{d.from} to {d.to}</span>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <Stat label="Unfiltered traffic" value={d.unfilteredTraffic} />
        <Stat label="Active traffic" value={d.activeTraffic} accent="green" />
        <Stat label="Guest sessions" value={d.guestSessions} />
        <Stat label="New signups" value={d.signupsCount} accent="green" />
        <Stat label="Guests converted" value={d.guestsConverted} />
        <Stat label="Newsletter opt-ins" value={d.optInCount} accent="green" />
      </div>

      <Card title="Daily traffic">
        <TrafficBars data={d.daily} />
      </Card>
    </div>
  );
}

function TrafficBars({ data }: { data: { date: string; unfiltered: number; active: number; guests: number }[] }) {
  if (data.length === 0) return <p className="text-muted mb-0">No traffic in selected range.</p>;
  const max = Math.max(1, ...data.map((d) => Math.max(d.unfiltered, d.active, d.guests)));
  return (
    <div>
      <div className="d-flex align-items-end gap-1" style={{ height: 160 }}>
        {data.map((d) => (
          <div key={d.date} className="flex-fill d-flex flex-column align-items-center gap-1"
            title={`${d.date}: unfiltered=${d.unfiltered} active=${d.active} guests=${d.guests}`}>
            <div className="d-flex w-100 flex-grow-1 align-items-end" style={{ gap: 1 }}>
              <div className="flex-fill bg-secondary rounded-top"
                style={{ height: `${(d.unfiltered / max) * 100}%`, minHeight: d.unfiltered > 0 ? 2 : 0 }} />
              <div className="flex-fill bg-success rounded-top"
                style={{ height: `${(d.active / max) * 100}%`, minHeight: d.active > 0 ? 2 : 0 }} />
              <div className="flex-fill bg-warning rounded-top"
                style={{ height: `${(d.guests / max) * 100}%`, minHeight: d.guests > 0 ? 2 : 0 }} />
            </div>
            <div className="text-muted" style={{ fontSize: 9 }}>{d.date.slice(5)}</div>
          </div>
        ))}
      </div>
      <div className="d-flex gap-3 small text-muted mt-2">
        <LegendDot color="bg-secondary" label="Unfiltered" />
        <LegendDot color="bg-success" label="Active" />
        <LegendDot color="bg-warning" label="Guests" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <span className={`d-inline-block ${color}`} style={{ width: 8, height: 8, borderRadius: 2 }} />
      {label}
    </span>
  );
}

// ============================================================================
// Pardot/MuleSoft endpoint config — admins edit URLs + credentials at runtime
// without redeploying. Empty fields fall back to appsettings:Pardot defaults.
// Client secret is masked on read; leave blank to keep existing value.
// ============================================================================
function PardotConfigTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin-pardot-config'],
    queryFn: () => api.get<PardotConfigResponse>('/api/v1/admin/pardot-config'),
  });

  const [form, setForm] = useState({
    accountCreatedUrl: '',
    salesRepUrl: '',
    clientId: '',
    clientSecret: '',
    campaignOverride: '',
    batchSize: '',
  });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!q.data || hydrated) return;
    setForm({
      accountCreatedUrl: q.data.overrides.accountCreatedUrl ?? '',
      salesRepUrl: q.data.overrides.salesRepUrl ?? '',
      clientId: q.data.overrides.clientId ?? '',
      clientSecret: '',
      campaignOverride: q.data.overrides.campaignOverride ?? '',
      batchSize: q.data.overrides.batchSize?.toString() ?? '',
    });
    setHydrated(true);
  }, [q.data, hydrated]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<PardotConfigResponse>('/api/v1/admin/pardot-config', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-pardot-config'] });
      setHydrated(false);
    },
  });

  if (q.isLoading) return <p className="text-muted">Loading…</p>;
  if (q.error || !q.data) return <p className="text-danger">Failed to load Pardot config.</p>;
  const d = q.data;

  return (
    <div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <Stat label="Account-created configured" value={d.accountCreatedConfigured ? 'Yes' : 'No'}
              accent={d.accountCreatedConfigured ? 'green' : undefined} />
            <Stat label="Sales-rep configured" value={d.salesRepConfigured ? 'Yes' : 'No'}
              accent={d.salesRepConfigured ? 'green' : undefined} />
          </div>
          <p className="mt-3 mb-0 text-muted small">
            Effective values are shown below. Leave a field blank to fall back to the
            appsettings default. Client Secret is hidden once saved — leave blank to keep
            the existing value.
          </p>
        </div>
      </div>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({
            accountCreatedUrl: form.accountCreatedUrl || null,
            salesRepUrl: form.salesRepUrl || null,
            clientId: form.clientId || null,
            clientSecret: form.clientSecret || null,
            campaignOverride: form.campaignOverride || null,
            batchSize: form.batchSize ? parseInt(form.batchSize, 10) : null,
          });
        }}
      >
        <div className="card-body d-flex flex-column gap-3">
        <PardotField label="Account-created URL"
          help={`Default: ${d.defaults.accountCreatedUrl ?? '(none)'} • Effective: ${d.effective.accountCreatedUrl ?? '(none)'}`}
          value={form.accountCreatedUrl}
          onChange={(v) => setForm({ ...form, accountCreatedUrl: v })} />
        <PardotField label="Sales-rep URL"
          help={`Default: ${d.defaults.salesRepUrl ?? '(none)'} • Effective: ${d.effective.salesRepUrl ?? '(none)'}`}
          value={form.salesRepUrl}
          onChange={(v) => setForm({ ...form, salesRepUrl: v })} />
        <PardotField label="Client ID"
          help={`Default: ${d.defaults.clientId ?? '(none)'} • Effective: ${d.effective.clientId ?? '(none)'}`}
          value={form.clientId}
          onChange={(v) => setForm({ ...form, clientId: v })} />
        <PardotField label="Client Secret (leave blank to keep existing)"
          type="password"
          help={`Currently: ${d.effective.clientSecret ?? '(not set)'}`}
          value={form.clientSecret}
          onChange={(v) => setForm({ ...form, clientSecret: v })} />
        <PardotField label="Salesforce Campaign override"
          help={`Default: ${d.defaults.campaignOverride ?? '(none)'} • Effective: ${d.effective.campaignOverride ?? '(none)'}`}
          value={form.campaignOverride}
          onChange={(v) => setForm({ ...form, campaignOverride: v })} />
        <PardotField label="Batch size"
          type="number"
          help={`Default: ${d.defaults.batchSize ?? '(none)'} • Effective: ${d.effective.batchSize ?? '(none)'}`}
          value={form.batchSize}
          onChange={(v) => setForm({ ...form, batchSize: v })} />
        </div>

        <div className="card-footer d-flex align-items-center gap-3">
          <button type="submit" disabled={save.isPending} className="btn btn-primary btn-sm">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {save.isError && <span className="small text-danger">Save failed.</span>}
          {save.isSuccess && <span className="small text-success">Saved.</span>}
        </div>
      </form>
    </div>
  );
}

function PardotField({
  label, help, value, onChange, type = 'text',
}: {
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="form-label small fw-medium mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="form-control form-control-sm"
      />
      {help && <div className="form-text" style={{ fontSize: 11 }}>{help}</div>}
    </div>
  );
}
